import {
  Center,
  getSize,
  Hide,
  Icon,
  Integer,
  Show,
  Spinner,
  ThemeIconKey,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

type ButtonIconSize = 'default' | 'small' | 'large';
type ButtonIconVariant = 'primary' | 'secondary' | 'inverted' | 'outline' | 'transparent';

export interface ButtonIconProps extends TestIdProps {
  icon?: ThemeIconKey;
  size?: ButtonIconSize;
  variant: ButtonIconVariant;
  isDisabled?: boolean;
  onClick?: VoidFunction;
  isLoading?: boolean;
}

interface StyledButtonIconProps {
  $isDisabled: boolean;
  $size: Integer;
  $variant: ButtonIconVariant;
}

const StyledButtonIcon = styled.button.attrs({type: 'button'})<StyledButtonIconProps>`
  outline: none;
  padding: 0;
  width: ${({$size}) => getSize($size)};
  height: ${({$size}) => getSize($size)};
  cursor: ${({$isDisabled}) => ($isDisabled ? 'auto' : 'pointer')};
  pointer-events: ${({$isDisabled}) => ($isDisabled ? 'none' : 'all')};
  opacity: ${({$isDisabled}) => ($isDisabled ? '0.5' : '1')};
  color: ${({$variant, theme}) =>
    match<ButtonIconVariant>($variant)
      .with('primary', always(theme.colors.fill.white))
      .with('secondary', always(theme.colors.accent.base))
      .with('inverted', always(theme.colors.accent.darkest))
      .with('outline', always(theme.colors.accent.darkest))
      .with('transparent', always(theme.colors.fill.white))
      .exhaustive()};
  background-color: ${({$variant, theme}) =>
    match<ButtonIconVariant>($variant)
      .with('primary', always(theme.colors.accent.base))
      .with('secondary', always(theme.colors.accent.lightest))
      .with('inverted', always(theme.colors.fill.white))
      .with('outline', always(theme.colors.general.transparent))
      .with('transparent', always(theme.colors.fill.overlayMedium))
      .exhaustive()};
  border-radius: 100%;
  border: ${({$variant, theme}) =>
    $variant === 'outline' ? `1px solid ${theme.colors.border.emphasisDefault}` : 'none'};

  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.border.emphasisDefault};
    outline-offset: 2px;
  }

  &:hover {
    color: ${({$variant, theme}) =>
      match<ButtonIconVariant>($variant)
        .with('primary', always(theme.colors.fill.white))
        .with('secondary', always(theme.colors.accent.base))
        .with('inverted', always(theme.colors.accent.base))
        .with('outline', always(theme.colors.accent.darkest))
        .with('transparent', always(theme.colors.fill.white))
        .exhaustive()};
    background-color: ${({$variant, theme}) =>
      match<ButtonIconVariant>($variant)
        .with('primary', always(theme.colors.accent.dark))
        .with('secondary', always(theme.colors.accent.lighter))
        .with('inverted', always(theme.colors.fill.white))
        .with('outline', always(theme.colors.border.emphasisDefault))
        .with('transparent', always(theme.colors.fill.overlayStrong))
        .exhaustive()};
  }
`;

export function ButtonIcon(props: ButtonIconProps) {
  const size = match<ButtonIconProps['size'], Integer>(props.size)
    .with('small', always(7))
    .with('large', always(14))
    .otherwise(always(11));
  const iconSize = match<ButtonIconProps['size'], Integer>(props.size)
    .with('large', always(6))
    .otherwise(always(4));
  const isDisabled = Boolean(props.isDisabled || props.isLoading);

  return (
    <StyledButtonIcon
      $isDisabled={isDisabled}
      $size={size}
      $variant={props.variant}
      data-testid={suffixTestId('buttonIcon', props)}
      disabled={isDisabled}
      onClick={props.onClick}
    >
      <Center width="100%" height="100%">
        <Show when={props.isLoading}>
          <Spinner size="small" />
        </Show>
        <Hide when={props.isLoading}>
          <Icon value={props.icon} size={iconSize} />
        </Hide>
      </Center>
    </StyledButtonIcon>
  );
}
